import {isEmpty, remove} from 'lodash';
import {Logger} from '../utils/Logger';
import {Injectable} from '@angular/core';
import {flatten} from '@angular/compiler';
import {ConfigService} from './config.service';
import {ErrorCode} from '../errors/error-code';
import {Portfolio} from '../model/portfolio.model';
import {Keys, StorageService} from './storage.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {PortfolioType} from '../model/portfolio-type.model';
import {GaugedInvestment} from '../model/gauged-investment.model';
import {PreferredCategories} from '../model/portfolio-preferences.model';
import {UnprocessableEntityError} from '../errors/UnprocessableEntityError';
import {PortfolioCreationRequest} from '../model/portfolio-create-request.model';
import {CustomHttpParamEncoder} from '../http/encoders/custom-http-param-encoder';
import {InsufficientWalletBalanceError} from '../errors/insufficient-wallet-balance.error';
import {PortfolioCreationData} from '../model/portfolio-creation-data/portfolio-creation-data.model';
import {AutoInvestPortfolioCreationData} from '../model/portfolio-creation-data/auto-invest-portfolio-creation.data';
import {ManualInvestPortfolioCreationData} from '../model/portfolio-creation-data/manual-invest-portfolio-creation-data.model';

@Injectable()
export class PortfolioService {
    private readonly httpClient: HttpClient;
    private readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private lenderPortfolio: any;
    private previousLenderId: string= '';

    constructor(httpClient: HttpClient, configService: ConfigService, storageService: StorageService) {
        this.httpClient = httpClient;
        this.configService = configService;
        this.storageService = storageService;
    }

    public async getPotentialInvestments(investmentAmount: number, lenderId: string, portfolioType: PortfolioType,
                                         preferredCategories: PreferredCategories[] = []): Promise<GaugedInvestment[]> {
        const url = this.configService.baseUrl() + `portfolio/previewInvestment`;
        let httpParams = new HttpParams({encoder: new CustomHttpParamEncoder()}).set('investment_amount', investmentAmount.toString())
            .set('portfolioType', portfolioType)
            .set('lender_id', lenderId);

        flatten(preferredCategories.map(preferredCategory => preferredCategory.ratingList))
            .forEach(rating => {
                httpParams = httpParams.append('rating', rating);
            });

        return await this.httpClient.get<GaugedInvestment[]>(url, {params: httpParams}).toPromise();
    }

    public async createPortfolio(gaugedInvestments, lenderLoanAgreementCheck: boolean, lenderExposureForBorrowerCheck: any,
                                 lenderExposureAcrossP2PPlatformsCheck: any, portfolioType: PortfolioType, investmentAmount: number,
                                 portfolioName: string, preferredCategories?: PreferredCategories[], finzyFlexPreLaunchAgreement?: boolean): Promise<void> {
        const url = this.configService.baseUrl() + `portfolio`;
        const portfolioCreationRequest: PortfolioCreationRequest = finzyFlexPreLaunchAgreement ? {
            portfolioName: portfolioName,
            profileId: this.storageService.getItem(Keys.PROFILE_ID),
            investmentAmount: investmentAmount,
            portfolioType: portfolioType,
            gaugedInvestments: gaugedInvestments,
            lenderConsents: {
                lenderLoanAgreementCheck: lenderLoanAgreementCheck,
                lenderExposureForBorrowerCheck: lenderExposureForBorrowerCheck,
                lenderExposureAcrossP2PPlatformsCheck: lenderExposureAcrossP2PPlatformsCheck,
                finzyFlexPreLaunchAgreement: finzyFlexPreLaunchAgreement,
            }
        } : {
            portfolioName: portfolioName,
            profileId: this.storageService.getItem(Keys.PROFILE_ID),
            investmentAmount: investmentAmount,
            portfolioType: portfolioType,
            gaugedInvestments: gaugedInvestments,
            lenderConsents: {
                lenderLoanAgreementCheck: lenderLoanAgreementCheck,
                lenderExposureForBorrowerCheck: lenderExposureForBorrowerCheck,
                lenderExposureAcrossP2PPlatformsCheck: lenderExposureAcrossP2PPlatformsCheck,
            }
        };

        if (preferredCategories) {
            portfolioCreationRequest.portfolioPreference = {
                preferredCategories: preferredCategories
            };
        }

        try {
            await this.httpClient.post(url, portfolioCreationRequest).toPromise();
        } catch (error) {
            if (error instanceof UnprocessableEntityError && error.code === ErrorCode.InsufficientWalletBalance) {
                Logger.log(`Wallet balance is insufficient to create portfolio of amount [${investmentAmount}].`);
                throw new InsufficientWalletBalanceError();
            } else {
                throw error;
            }
        }
    }

    public async createOrUpdateLocalPortfolioCreationData(portfolioCreationData: PortfolioCreationData): Promise<void> {
        const existingPortfolioCreationDataListFromStorage = this.storageService.getObjectFromSession<any[]>(Keys.MANUAL_INVEST_PORTFOLIO_CREATION);
        const existingPortfolioCreationDataList = existingPortfolioCreationDataListFromStorage ? existingPortfolioCreationDataListFromStorage : [];
        remove(existingPortfolioCreationDataList, item => item.tempPortfolioId === portfolioCreationData.tempPortfolioId);
        existingPortfolioCreationDataList.push(portfolioCreationData);
        this.storageService.saveObjectToSession(Keys.MANUAL_INVEST_PORTFOLIO_CREATION, existingPortfolioCreationDataList);
    }

    public async getLocalPortfolioCreationData(tempPortfolioId?: string): Promise<ManualInvestPortfolioCreationData | AutoInvestPortfolioCreationData> {
        const portfolioCreationDataListFromStorage = this.storageService.getObjectFromSession<any[]>(Keys.MANUAL_INVEST_PORTFOLIO_CREATION);
        const portfolioCreationDataList = portfolioCreationDataListFromStorage ? portfolioCreationDataListFromStorage : [];
        const portfolioCreationData = portfolioCreationDataList.find(p => p.tempPortfolioId === tempPortfolioId);
        if (portfolioCreationData) {
            return portfolioCreationData.portfolioType === PortfolioType.AutoInvest ?
                AutoInvestPortfolioCreationData.from(portfolioCreationData) :
                ManualInvestPortfolioCreationData.from(portfolioCreationData);
        } else {
            return portfolioCreationData;
        }
    }

    public async removeLocalPortfolioCreationData(tempPortfolioId: string): Promise<void> {
        const portfolioCreationDataList = this.storageService.getObjectFromSession<any[]>(Keys.MANUAL_INVEST_PORTFOLIO_CREATION);
        if (!isEmpty(portfolioCreationDataList)) {
            remove(portfolioCreationDataList, item => item.tempPortfolioId === tempPortfolioId);
            this.storageService.saveObjectToSession(Keys.MANUAL_INVEST_PORTFOLIO_CREATION, portfolioCreationDataList);
        }
    }

    public async getPortfoliosByLenderId(lenderId: string): Promise<any[]> {
        const url = this.configService.baseUrl() + `getLenderPortfolio`;
        const response = await this.httpClient.post(url, {lender_id: lenderId}).toPromise();
        this.lenderPortfolio = response['portfolio_details'];
        return this.lenderPortfolio;
    }

    public async getLenderPortfoliosInfo (lenderId: string): Promise<any[]>  {
        if(this.previousLenderId != lenderId) {
            this.previousLenderId = lenderId;
            this.lenderPortfolio = await this.getPortfoliosByLenderId(lenderId);
            return this.lenderPortfolio;
        } else {
            if(this.lenderPortfolio === undefined) {
                await this.getPortfoliosByLenderId(lenderId);
            }
            return this.lenderPortfolio;
        }
    }

    public async getPortfolio(portfolioId: string): Promise<Portfolio> {
        const url = `${this.configService.baseUrl()}portfolio/${portfolioId}`;
        return await this.httpClient.get<Portfolio>(url).toPromise();
    }

    public async getPortfoliosByIds(portfolioIds: string[]): Promise<Portfolio[]> {
        const url = `${this.configService.baseUrl()}portfolios?id=${portfolioIds}`;
        return await this.httpClient.get<Portfolio[]>(url).toPromise();
    }

    async getPortfolioMetrics(portfolioId: string): Promise<any> {
        const url = `${this.configService.baseUrl()}portfolio/${portfolioId}/metrics`;
        return await this.httpClient.get(url).toPromise();
    }

    public async reverseInvestment(portfolioId: string, loanId: string): Promise<void> {
        await this.httpClient.post(`${this.configService.baseUrl()}portfolio/${portfolioId}/reverse-investment`,
            {loanId: loanId}).toPromise();
    }
}
